// OutstandingPanel — numbered tabbed pages of outstanding tasks
// (README §6.7.3 item 1).
//
// The caller passes `items` already ordered by end time (`mustCompleteBy`
// ascending) — the order tasks time out. Each numbered tab is a page;
// selecting it shows that task with its times. Completing a task shrinks
// the list and the rest renumber up.
//
// In-order lock: only the FIRST (oldest — tab 1) outstanding task carries
// a checkbox, so the backlog is cleared oldest-first. The other tabs stay
// navigable for preview but show no checkbox until they become the oldest.
// (This supersedes the earlier "any tab is checkable" rule — DECISIONS
// §E.1.) The count is not shown as text; it survives in each tab's "task n
// of N" accessible name for screen readers.
//
// Option A palette (black / white) per the locked palette decision. When
// empty the panel collapses to a thin "Nothing outstanding" strip — quiet
// but never absent.
//
// Accessibility: a `tablist` of numbered tabs drives a `tabpanel` showing
// the selected task with its checkbox. `←` / `→` move the selection; the
// region is an `aria-live` polite area so additions and removals are
// announced.

import { useEffect, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { theme } from '../theme';
import { Checkbox } from './Checkbox';
import type { OverrunItem } from './OverrunBanner';

/** Structurally identical to OverrunBanner's item — reused so the panes
 *  can feed either component from one mapping. `phaseChip` is retained on
 *  the type but no longer rendered (each panel is single-phase). */
export type OutstandingItem = OverrunItem;

export interface OutstandingPanelProps {
  /** Outstanding tasks, already ordered by end time. */
  items: OutstandingItem[];
  onCheck: (id: string) => void;
}

export function OutstandingPanel({ items, onCheck }: OutstandingPanelProps) {
  const palette = theme.overrunBanner;
  const [selected, setSelected] = useState(0);

  // Keep the selection in range as tabs are completed and the list
  // shrinks. Default focus stays on tab ① (the longest-overdue task).
  useEffect(() => {
    if (selected > Math.max(0, items.length - 1)) setSelected(Math.max(0, items.length - 1));
  }, [items.length, selected]);

  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  if (items.length === 0) {
    return (
      <div
        role="status"
        aria-live="polite"
        aria-label="Outstanding panel, nothing outstanding"
        style={{
          background: palette.bg,
          color: palette.text,
          padding: '0.4rem 1rem',
          borderRadius: '6px',
          fontSize: '0.85rem',
          opacity: 0.7,
        }}
      >
        Nothing outstanding.
      </div>
    );
  }

  const index = Math.min(selected, items.length - 1);
  const current = items[index]!;

  const move = (delta: number): void => {
    const n = items.length;
    const next = (((index + delta) % n) + n) % n;
    setSelected(next);
    tabRefs.current[next]?.focus();
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>): void => {
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      move(-1);
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      move(1);
    }
  };

  return (
    <div
      role="region"
      aria-live="polite"
      aria-label={`Outstanding panel, ${items.length} task${items.length === 1 ? '' : 's'} outstanding`}
      data-cc-outstanding-panel=""
      tabIndex={0}
      onKeyDown={handleKeyDown}
      style={{
        background: palette.bg,
        color: palette.text,
        padding: '0.6rem 1rem',
        borderRadius: '8px',
        display: 'flex',
        flexDirection: 'column',
        gap: '0.5rem',
      }}
    >
      {/* Numbered page-tab strip. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <div
          role="tablist"
          aria-label="Outstanding tasks, in order"
          aria-orientation="horizontal"
          style={{ display: 'flex', gap: '0.25rem', flex: 1, flexWrap: 'wrap' }}
        >
          {items.map((item, i) => {
            const isSel = i === index;
            return (
              <button
                key={item.id}
                ref={(el) => {
                  tabRefs.current[i] = el;
                }}
                type="button"
                role="tab"
                aria-selected={isSel}
                aria-label={`Outstanding task ${i + 1} of ${items.length}: ${item.label}`}
                tabIndex={isSel ? 0 : -1}
                onClick={() => setSelected(i)}
                style={{
                  minHeight: '44px',
                  padding: '0 0.7rem',
                  background: 'transparent',
                  color: palette.text,
                  border: 'none',
                  borderBottom: `3px solid ${isSel ? palette.badge : 'transparent'}`,
                  fontWeight: isSel ? 700 : 500,
                  fontVariantNumeric: 'tabular-nums',
                  fontSize: '1rem',
                  cursor: 'pointer',
                  opacity: isSel ? 1 : 0.7,
                }}
              >
                {i + 1}
              </button>
            );
          })}
        </div>
      </div>

      {/* Selected page: task on the left, checkbox on the right. */}
      <div
        role="tabpanel"
        aria-label={`Outstanding task ${index + 1}`}
        style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', minHeight: '44px' }}
      >
        <span style={{ flex: 1, fontSize: '1rem', fontWeight: 600 }}>
          {current.label}
          {current.time && current.endTime && (
            <span
              style={{
                marginLeft: '0.5rem',
                fontWeight: 400,
                fontSize: '0.85rem',
                opacity: 0.8,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {current.time} / {current.endTime}
              {current.overdueLabel ? ` (${current.overdueLabel})` : ''}
            </span>
          )}
        </span>
        {index === 0 && (
          <Checkbox
            state="unchecked"
            label={`Mark "${current.label}" complete`}
            onChange={() => onCheck(current.id)}
          />
        )}
      </div>
    </div>
  );
}

export default OutstandingPanel;
